import { Button, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { BaseItem as ZhBaseItem, DetailHeader as ZhDetailHeader, Grid as ZhGrid } from "zhihao-ui-react";

import request from "@/api";
import { fileService } from "@/api/services/fileService";
import type { ActivityProductDetail, ActivityProductDetailResult } from "./types";

const pricingMethodLabels: Record<number, string> = {
    1: "市场价+活动单价",
    2: "比市场价直降",
    3: "以活动现场公布为准"
};
const overbookingLabels: Record<number, string> = { 0: "否", 1: "是" };
const orderRuleLabels: Record<number, string> = { 1: "固定金额", 2: "阶梯金额" };

export interface ActivityProductDetailPageProps {
    loadProductDetail?: (code: string) => Promise<ActivityProductDetailResult>;
}

/** 复用 Vue 活动商品详情接口。 */
export async function loadActivityProductDetail(code: string): Promise<ActivityProductDetailResult> {
    const response = await request.service.get<ActivityProductDetailResult>(`/admin/product-campaign/${code}`);
    return response.data;
}

const formatNumber = (value?: number) => (value == null ? "-" : String(value));
const formatStock = (value?: number) => (value == null ? "-" : value.toLocaleString("en-US"));
const formatMoney = (value?: number) => (value == null ? "-" : value.toFixed(2));
const formatFileIds = (files: Array<{ id?: number | string }>) =>
    files.map(file => (file.id == null ? "-" : String(file.id))).join("、");

/** 商品活动详情页，按 Vue 详情字段展示，不在此页改变商品状态。 */
export function ActivityProductDetailPage({
    loadProductDetail = loadActivityProductDetail
}: ActivityProductDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const code = new URLSearchParams(location.search).get("code") || "";
    const [detail, setDetail] = useState<ActivityProductDetail>();
    const [pictureFiles, setPictureFiles] = useState<Array<{ id?: number | string; path?: string }>>([]);
    const [videoFiles, setVideoFiles] = useState<Array<{ id?: number | string; path?: string }>>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // Vue 活动商品接口的枚举可能以字符串返回，先统一为数字再决定详情分支。
    const pricingMethod = detail?.pricingMethod == null ? undefined : Number(detail.pricingMethod);
    // Vue 活动商品下单规则同样可能返回字符串，统一后再展示对应意向金结构。
    const orderRule = detail?.orderRule == null ? undefined : Number(detail.orderRule);
    // Vue 活动商品超售标记兼容带空白的字符串枚举，避免详情显示缺省值。
    const hasOverbooking = detail?.hasOverbooking == null ? undefined : Number(detail.hasOverbooking);

    useEffect(() => {
        if (!code) return;
        void loadProductDetail(code)
            .then(result => {
                setErrorMessage(null);
                setDetail(result.productCampaign);
                const pictureIds = (result.productCampaign?.pictureFileIds || []).map(Number).filter(Number.isInteger);
                const videoIds =
                    result.productCampaign?.videoFileId == null ? [] : [Number(result.productCampaign.videoFileId)];
                // 先展示旧 Vue 接口返回的文件 ID，避免文件详情接口较慢或失败时出现空白内容。
                setPictureFiles(pictureIds.map(id => ({ id })));
                setVideoFiles(videoIds.map(id => ({ id })));
                void Promise.all([
                    pictureIds.length ? fileService.getFileByIds(pictureIds) : Promise.resolve([]),
                    videoIds.length ? fileService.getFileByIds(videoIds) : Promise.resolve([])
                ])
                    .then(([pictures, videos]) => {
                        setPictureFiles(pictures);
                        setVideoFiles(videos);
                    })
                    .catch(() => {
                        // 文件详情失败时保留已展示的文件 ID，避免产生未处理的异步异常。
                    });
            })
            .catch(() => setErrorMessage("活动商品详情加载失败"));
    }, [code, loadProductDetail]);

    return (
        <section aria-label="activity-product-detail-page" className="detail-page-transparent admin-detail-page">
            <div className="section-card admin-detail-header-card">
                <ZhDetailHeader leftInfos={[{ label: "商品编号", value: code || "-" }]} title="活动商品详情" />
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
            </div>
            <div className="section-card activity-product-detail-content">
                <ZhGrid className="activity-product-detail-grid" cols={3} gap={[12, 12]}>
                    <ZhBaseItem
                        label="供应商"
                        value={
                            detail?.supplierEnterprise?.name ||
                            detail?.supplierEnterprise?.enterpriseName ||
                            detail?.supplierName ||
                            "-"
                        }
                    />
                    <ZhBaseItem label="活动商品" value={detail?.name || "-"} />
                    <ZhBaseItem label="定价方式" value={pricingMethodLabels[pricingMethod || 0] || "-"} />
                    {pricingMethod === 1 ? (
                        <>
                            <ZhBaseItem label="市场价" value={`${formatNumber(detail?.marketPrice)}元/吨`} />
                            <ZhBaseItem label="活动单价" value={`${formatNumber(detail?.estimatedUnitPrice)}元/吨`} />
                        </>
                    ) : null}
                    {pricingMethod === 2 ? (
                        <ZhBaseItem label="比市场直降" value={`${formatNumber(detail?.lowerThanMarketPrice)}元/吨`} />
                    ) : null}
                    <ZhBaseItem label="库存量" value={`${formatStock(detail?.stock)}吨`} />
                    <ZhBaseItem label="是否允许超售" value={overbookingLabels[hasOverbooking ?? -1] || "-"} />
                    <ZhBaseItem label="起订吨数" value={`${formatNumber(detail?.minOrderTon)}吨`} />
                    <ZhBaseItem label="最小调整吨数" value={`${formatNumber(detail?.minAdjustmentTon)}吨`} />
                    <ZhBaseItem label="下单要求" value={orderRuleLabels[orderRule || 0] || "-"} />
                    {orderRule === 1 ? (
                        <ZhBaseItem label="意向金" value={`${formatMoney(detail?.intentionDeposit)}元`} />
                    ) : null}
                    {orderRule === 2 ? (
                        <ZhBaseItem
                            label="最低意向金"
                            value={`${formatMoney(detail?.intentionDeposit)}元（每增加${formatNumber(detail?.tonStep)}吨增加${formatMoney(detail?.amountStep)}元）`}
                        />
                    ) : null}
                    <ZhBaseItem
                        label="图片"
                        span={3}
                        value={
                            pictureFiles.length && pictureFiles.some(file => file.path) ? (
                                <div className="activity-product-detail-media">
                                    <img alt="图片" src={pictureFiles[0]?.path} />
                                </div>
                            ) : pictureFiles.length ? (
                                `图片文件：${formatFileIds(pictureFiles)}`
                            ) : (
                                "暂无图片"
                            )
                        }
                    />
                    <ZhBaseItem
                        label="视频"
                        span={3}
                        value={
                            videoFiles.length && videoFiles.some(file => file.path) ? (
                                <div className="activity-product-detail-media">
                                    <video controls src={videoFiles[0]?.path} />
                                </div>
                            ) : videoFiles.length ? (
                                `视频文件：${formatFileIds(videoFiles)}`
                            ) : (
                                "暂无视频"
                            )
                        }
                    />
                    <ZhBaseItem label="商品详情" span={3}>
                        {detail?.detail ? <div dangerouslySetInnerHTML={{ __html: detail.detail }} /> : "-"}
                    </ZhBaseItem>
                </ZhGrid>
            </div>
            {/* 详情页操作固定在底部，和 Vue activity-product-detail 保持一致。 */}
            <Space className="operation-fixed-btn" size={0}>
                <Button onClick={() => void navigate(-1)}>返回</Button>
            </Space>
        </section>
    );
}
